import React from 'react';
import {
  Menu,
  Sun,
  Moon,
  Plus,
  ExternalLink,
} from 'lucide-react';
import { useTheme } from '../../shared/context/ThemeContext';
import { Button } from '../../shared/components/Button';
import { NavRoute } from './Sidebar';
import { UNIVERSITY_CONFIG } from '../../config/university.config';

interface TopBarProps {
  currentRoute: NavRoute;
  isSidebarOpen?: boolean;
  onToggleSidebar?: () => void;
  onToggleMobileSidebar?: () => void;
  onOpenQuickCreate: () => void;
}

const routeTitles: Record<NavRoute, { title: string; category: string }> = {
  dashboard: { title: 'Dashboard', category: 'Overview' },
  'cover-studio': { title: 'Cover Studio', category: 'Design' },
  'report-studio': { title: 'Report Studio', category: 'Authoring' },
  'document-studio': { title: 'Document Studio', category: 'PDF Tools' },
  templates: { title: 'Templates', category: 'Library' },
  settings: { title: 'Settings', category: 'Preferences' },
};

export const TopBar: React.FC<TopBarProps> = ({
  currentRoute,
  isSidebarOpen,
  onToggleSidebar,
  onToggleMobileSidebar,
  onOpenQuickCreate,
}) => {
  const { theme, toggleTheme } = useTheme();
  const currentInfo = routeTitles[currentRoute] || { title: 'Studio', category: 'Workspace' };

  const handleToggle = () => {
    if (onToggleSidebar) onToggleSidebar();
    else if (onToggleMobileSidebar) onToggleMobileSidebar();
  };

  return (
    <header className="app-topbar">
      <div className="topbar-left">
        <button
          className="icon-btn"
          onClick={handleToggle}
          aria-label="Toggle navigation menu"
          aria-expanded={isSidebarOpen}
          title="Toggle navigation menu"
          style={{ display: 'flex' }}
        >
          <Menu size={18} />
        </button>

        <nav aria-label="Breadcrumb" className="topbar-breadcrumbs">
          <span style={{ color: 'var(--text-tertiary)' }}>{currentInfo.category}</span>
          <span style={{ color: 'var(--text-tertiary)' }}>/</span>
          <span className="breadcrumb-active">{currentInfo.title}</span>
        </nav>
      </div>

      <div className="topbar-right">
        {/* Quick External University Portal Link */}
        {UNIVERSITY_CONFIG.website && (
          <a
            href={UNIVERSITY_CONFIG.website}
            target="_blank"
            rel="noreferrer"
            className="btn btn-ghost btn-sm"
            title={`Visit ${UNIVERSITY_CONFIG.name} portal`}
            style={{ display: 'none', alignItems: 'center', gap: 4 }}
          >
            <span>{UNIVERSITY_CONFIG.shortName}</span>
            <ExternalLink size={13} />
          </a>
        )}

        {/* Theme Toggle */}
        <button
          className="icon-btn"
          onClick={toggleTheme}
          aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
          title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
        >
          {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
        </button>

        {/* Global Quick Create Action */}
        <Button
          variant="primary"
          size="sm"
          icon={<Plus size={16} />}
          onClick={onOpenQuickCreate}
        >
          New Document
        </Button>
      </div>
    </header>
  );
};
